import React from "react";
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";
import { AppFile } from "../../types/user";
import { formatDateLong } from "../../utils/dateUtils";

export interface LightboxSlide {
  src: string;
  alt: string;
  description?: React.ReactNode;
}

interface ImageLightboxProps {
  open: boolean;
  index: number;
  slides: LightboxSlide[];
  onClose: () => void;
}

// When an image was taken: uploaded_at when the API supplies it, created_at
// otherwise. Formatted in UTC, so the day matches the server's calendar day
// rather than the viewer's timezone.
export const fileCaptionDate = (file: AppFile): string =>
  formatDateLong(file.uploaded_at || file.created_at, "Date not available");

/**
 * Slides for records from the API's polymorphic `files` table - progress
 * images, measurement attachments. Files without a usable url are skipped, so
 * build the thumbnails you show from the same filtered list or the indexes
 * will not line up. `prefix` labels the slide, e.g. the measurement type.
 */
export const buildFileSlides = (
  files: AppFile[],
  prefix?: string,
  recordDate?: string
): LightboxSlide[] =>
  files
    .filter((file) => !!file.full_path)
    .map((file) => {
      const date = recordDate
        ? formatDateLong(recordDate, "Date not available")
        : fileCaptionDate(file);
      return {
        src: file.full_path as string,
        alt: file.original_name,
        description: (
          <p className="mb-2">{prefix ? `${prefix} — ${date}` : date}</p>
        ),
      };
    });

// Every gallery in the dashboard opens the same lightbox: a finite carousel
// with captions on and the caption toggle available. Pair it with
// useImageLightbox, which owns the open/index/slides state.
const ImageLightbox: React.FC<ImageLightboxProps> = ({
  open,
  index,
  slides,
  onClose,
}) => (
  <Lightbox
    open={open}
    close={onClose}
    index={index}
    slides={slides}
    plugins={[Captions]}
    carousel={{ finite: true }}
    captions={{ showToggle: true, descriptionTextAlign: "center" }}
  />
);

export default ImageLightbox;
